import React, { useCallback, useState } from "react";
import { inject, observer } from "mobx-react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import EmojiAnimationStore from "../_stores/emoji-animation/EmojiAnimationStore";

type Props = {
  emojiAnimationStore?: EmojiAnimationStore;
};

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: () => void;
}> = ({ checked, onChange }) => (
  <div
    onClick={e => {
      e.stopPropagation();
      onChange();
    }}
    style={{
      width: 36,
      height: 20,
      borderRadius: 10,
      background: checked ? "#22c55e" : "#4b5563",
      cursor: "pointer",
      position: "relative",
      transition: "background 0.25s ease",
      flexShrink: 0,
      marginLeft: 14,
    }}>
    <div
      style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: "#fff",
        position: "absolute",
        top: 2,
        left: checked ? 18 : 2,
        transition: "left 0.25s ease",
        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
      }}
    />
  </div>
);

const EmojiSettingsPopover: React.ComponentType<Props & WrappedComponentProps> = ({
  emojiAnimationStore,
  intl,
}) => {
  const [open, setOpen] = useState(false);

  const {
    floatingEffectsEnabled,
    soundEffectsEnabled,
    toggleFloatingEffects,
    toggleSoundEffects,
  } = emojiAnimationStore!;

  const toggle = useCallback(() => setOpen(prev => !prev), []);

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      {open && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10999,
            }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              bottom: "calc(100% + 8px)",
              right: -4,
              background: "rgba(24, 26, 32, 0.92)",
              borderRadius: 10,
              padding: "10px 14px",
              zIndex: 11000,
              minWidth: 200,
              boxShadow: "0 6px 28px rgba(0,0,0,0.55)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: 10,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}>
              <span
                style={{
                  color: "#e5e7eb",
                  fontSize: 13,
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                }}>
                {intl.formatMessage({
                  id: "broadcast-system-settings-control.floating-effect",
                  defaultMessage: "Floating Effect",
                })}
              </span>
              <ToggleSwitch
                checked={floatingEffectsEnabled}
                onChange={toggleFloatingEffects}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingTop: 10,
              }}>
              <span
                style={{
                  color: "#e5e7eb",
                  fontSize: 13,
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                }}>
                {intl.formatMessage({
                  id: "broadcast-system-settings-control.sound-effects",
                  defaultMessage: "Sound Effects",
                })}
              </span>
              <ToggleSwitch
                checked={soundEffectsEnabled}
                onChange={toggleSoundEffects}
              />
            </div>
          </div>
        </>
      )}
      <button
        onClick={toggle}
        style={{
          background: "rgba(0, 0, 0, 0.45)",
          border: "2px solid #ffffff",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderRadius: "50%",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#ffffff",
          fontSize: 22,
          width: 30,
          height: 30,
          padding: 0,
          lineHeight: 1,
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.6)",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
        }}>
        ⋮
      </button>
    </div>
  );
};

export default injectIntl(
  inject("emojiAnimationStore")(observer(EmojiSettingsPopover))
);
